import React, { useState, useEffect, useCallback } from 'react';
import { companionService, Companion } from '../services/companionService';
import EditCompanionModal from './EditCompanionModal';
import LoadingScreen from './ui/LoadingScreen';
import DebugInfo from './DebugInfo';

interface AdminDashboardProps {}

const AdminDashboard: React.FC<AdminDashboardProps> = () => {
  const [companions, setCompanions] = useState<Companion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCompanion, setSelectedCompanion] = useState<Companion | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'age' | 'categories'>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const loadCompanions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await companionService.getAllCompanions();
      setCompanions(response.companions);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load companions:', err);
      setError('Failed to load companions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanions();
  }, [loadCompanions]);

  const handleEditCompanion = (companion: Companion) => {
    setSelectedCompanion(companion);
    setShowEditModal(true);
  };

  const handleDeleteCompanion = async (companionId: string) => {
    if (!window.confirm('Are you sure you want to delete this companion? This action cannot be undone.')) {
      return;
    }

    try {
      // Try to delete via API - will work when backend implements the endpoint
      await companionService.deleteCompanion(companionId);
      
      // Update the local state instead of refreshing the entire list
      setCompanions(prevCompanions => 
        prevCompanions.filter(companion => 
          companion.companion_id !== companionId
        )
      );
    } catch (err: any) {
      console.error('Failed to delete companion:', err);
      // If API not implemented yet, show helpful message
      if (err.status === 404 || err.status === 405) {
        alert('Delete API endpoint not yet implemented on the backend. The companion would be deleted once the backend supports it.');
      } else {
        alert('Failed to delete companion. Please try again.');
      }
    }
  };

  const handleSaveCompanion = async (updatedData: any) => {
    if (!selectedCompanion) return;

    try {
      // Try to update via API - use the correct companion_id field
      const companionId = selectedCompanion.companion_id;
      if (!companionId) {
        throw new Error('No valid companion ID found');
      }
      await companionService.updateCompanion(companionId, updatedData);
      
      // Update the local state with proper structure transformation
      setCompanions(prevCompanions => 
        prevCompanions.map(companion => 
          companion.companion_id === companionId 
            ? {
                ...companion,
                // Transform the flat updatedData back to nested structure
                name: {
                  first_name: updatedData.first_name || companion.name?.first_name || null,
                  last_name: updatedData.last_name || companion.name?.last_name || null,
                  nick_name: updatedData.nick_name || companion.name?.nick_name || null
                },
                description: updatedData.description ?? companion.description,
                about: updatedData.about ?? companion.about,
                birthday: updatedData.birthday ?? companion.birthday,
                age: updatedData.age ?? companion.age,
                is_public: updatedData.is_public ?? companion.is_public,
                categories: updatedData.categories ?? companion.categories,
                attributes: updatedData.attributes ? {
                  physical: {
                    ...companion.attributes?.physical,
                    ...updatedData.attributes.physical
                  },
                  personal: {
                    ...companion.attributes?.personal,
                    ...updatedData.attributes.personal
                  }
                } : companion.attributes,
                traits: updatedData.traits ?? companion.traits,
                personality: updatedData.personality ?? companion.personality
              }
            : companion
        )
      );
      
      // Don't refresh the entire page - just close the modal
    } catch (err: any) {
      console.error('Failed to update companion:', err);
      // If API not implemented yet, show helpful message
      if (err.status === 404 || err.status === 405) {
        alert('Update API endpoint not yet implemented on the backend. The companion data would be updated once the backend supports it.');
      } else {
        throw err; // Re-throw other errors to let the modal handle them
      }
    }
  };

  const handleToggleActive = async (companion: Companion) => {
    try {
      // Try to toggle via API - use the correct companion_id field
      const companionId = companion.companion_id;
      if (!companionId) {
        throw new Error('No valid companion ID found');
      }
      await companionService.toggleCompanionStatus(companionId, !companion.is_public);
      
      // Update the local state instead of refreshing the entire list
      setCompanions(prevCompanions => 
        prevCompanions.map(comp => 
          comp.companion_id === companionId 
            ? { ...comp, is_public: !comp.is_public }
            : comp
        )
      );
    } catch (err: any) {
      console.error('Failed to toggle companion status:', err);
      // If API not implemented yet, show helpful message
      if (err.status === 404 || err.status === 405) {
        alert('Toggle status API endpoint not yet implemented on the backend.');
      } else {
        alert('Failed to toggle companion status. Please try again.');
      }
    }
  };

  const filteredAndSortedCompanions = companions
    .filter(companion => {
      // Handle new name structure
      const displayName = typeof companion.name === 'object' && companion.name
        ? (companion.name.nick_name || `${companion.name.first_name || ''} ${companion.name.last_name || ''}`.trim() || 'Unknown')
        : String(companion.name || 'Unknown');
      const nameMatch = displayName.toLowerCase().includes(searchTerm.toLowerCase());
      const descriptionMatch = (companion.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      let personalityMatch = false;
      if (companion.personality) {
        personalityMatch = JSON.stringify(companion.personality).toLowerCase().includes(searchTerm.toLowerCase());
      }
      
      return nameMatch || descriptionMatch || personalityMatch;
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      if (sortBy === 'name') {
        // Handle new name structure
        const aName = typeof a.name === 'object' && a.name
          ? (a.name.nick_name || `${a.name.first_name || ''} ${a.name.last_name || ''}`.trim() || 'Unknown')
          : String(a.name || 'Unknown');
        const bName = typeof b.name === 'object' && b.name
          ? (b.name.nick_name || `${b.name.first_name || ''} ${b.name.last_name || ''}`.trim() || 'Unknown')
          : String(b.name || 'Unknown');
        aValue = aName.toLowerCase();
        bValue = bName.toLowerCase();
      } else if (sortBy === 'age') {
        aValue = a.age || 0;
        bValue = b.age || 0;
      } else if (sortBy === 'categories') {
        aValue = a.categories?.length || 0;
        bValue = b.categories?.length || 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900 p-6">
        <div className="flex justify-center items-center h-96">
          <LoadingScreen
            title="Loading Admin Dashboard"
            message="Preparing admin interface..."
            variant="inline"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 p-6">
      <DebugInfo />
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Admin Dashboard</h1>
        <p className="text-dark-300">Manage all AI companions and their settings</p>
      </div>

      {/* Controls */}
      <div className="glass-effect rounded-2xl p-6 border border-dark-600 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search companions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-accent-purple"
            />
          </div>

          {/* Sort Controls */}
          <div className="flex gap-2">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-accent-purple"
            >
              <option value="name">Name</option>
              <option value="created_at">Created Date</option>
              <option value="likes_count">Likes</option>
              <option value="messages_count">Messages</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white hover:bg-dark-700 transition-colors"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          {/* Stats */}
          <div className="text-dark-300 text-sm">
            {filteredAndSortedCompanions.length} of {companions.length} companions
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="glass-effect rounded-2xl p-6 border border-red-600 mb-6">
          <div className="text-red-400 text-center">
            <div className="text-2xl mb-2">⚠️</div>
            <p>{error}</p>
            <button
              onClick={loadCompanions}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Companions Table */}
      <div className="glass-effect rounded-2xl border border-dark-600 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-800">
              <tr>
                <th className="px-6 py-4 text-left text-white font-semibold">Avatar</th>
                <th className="px-6 py-4 text-left text-white font-semibold">Name & Info</th>
                <th className="px-6 py-4 text-left text-white font-semibold">Occupation & Hobby</th>
                <th className="px-6 py-4 text-left text-white font-semibold">Physical</th>
                <th className="px-6 py-4 text-left text-white font-semibold">Status</th>
                <th className="px-6 py-4 text-left text-white font-semibold">Public</th>
                <th className="px-6 py-4 text-center text-white font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedCompanions.map((companion, index) => (
                <tr 
                  key={companion.companion_id || index} 
                  className={`border-t border-dark-600 hover:bg-dark-800/50 transition-colors ${
                    index % 2 === 0 ? 'bg-dark-900/50' : 'bg-dark-800/20'
                  }`}
                >
                  {/* Avatar */}
                  <td className="px-6 py-4">
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-dark-700">
                      {companion.assets?.primary_photo ? (
                        <img 
                          src={companion.assets?.primary_photo?.presigned_url || ''} 
                          alt={typeof companion.name === 'object' && companion.name
                            ? (companion.name.nick_name || `${companion.name.first_name || ''} ${companion.name.last_name || ''}`.trim() || 'Unknown')
                            : String(companion.name || 'Unknown')}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                            (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full flex items-center justify-center text-dark-400 ${companion.assets?.primary_photo ? 'hidden' : ''}`}>
                        👤
                      </div>
                    </div>
                  </td>

                  {/* Name */}
                  <td className="px-6 py-4">
                    <div className="text-white font-medium">
                      {typeof companion.name === 'object' && companion.name
                        ? (companion.name.nick_name || `${companion.name.first_name || ''} ${companion.name.last_name || ''}`.trim() || 'Unknown')
                        : String(companion.name || 'Unknown')}
                    </div>
                    <div className="text-dark-400 text-sm">Age: {companion.age || 'N/A'}</div>
                    <div className="text-dark-400 text-xs">Categories: {companion.categories?.join(', ') || 'N/A'}</div>
                  </td>

                  {/* Personality & Description */}
                  <td className="px-6 py-4">
                    <div className="text-white text-sm">
                      <div className="font-medium mb-1">
                        {companion.attributes?.personal?.occupation || 'N/A'}
                      </div>
                      <div className="text-dark-400 text-xs">
                        Hobby: {companion.attributes?.personal?.hobby || 'N/A'}
                      </div>
                    </div>
                    <div className="text-dark-400 text-xs truncate max-w-xs mt-1">
                      {companion.description || 'No description'}
                    </div>
                  </td>

                  {/* Physical Attributes */}
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="text-white text-xs">
                        {companion.attributes?.physical?.ethnicity || 'N/A'}
                      </div>
                      <div className="text-dark-400 text-xs">
                        {companion.attributes?.physical?.hair_color || 'N/A'} hair
                      </div>
                      <div className="text-dark-400 text-xs">
                        {companion.attributes?.physical?.eye_color || 'N/A'} eyes
                      </div>
                    </div>
                  </td>

                  {/* Status */}
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="text-white text-xs">
                        {companion.assets?.primary_video ? '🎥 Video' : '📷 Photo'}
                      </div>
                      <div className="text-dark-400 text-xs">
                        {(companion.assets?.gallery?.length || 0)} gallery assets
                      </div>
                    </div>
                  </td>

                  {/* Public Status */}
                  <td className="px-6 py-4">
                    <button
                      onClick={() => handleToggleActive(companion)}
                      className={`px-2 py-1 rounded-full text-xs font-medium transition-colors hover:opacity-80 ${
                        companion.is_public 
                          ? 'bg-green-600/20 text-green-400 border border-green-600/30 hover:bg-green-600/30' 
                          : 'bg-red-600/20 text-red-400 border border-red-600/30 hover:bg-red-600/30'
                      }`}
                      title="Click to toggle public/private status"
                    >
                      {companion.is_public ? 'Public' : 'Private'}
                    </button>
                  </td>

                  {/* Actions */}
                  <td className="px-6 py-4">
                    <div className="flex gap-2 justify-center">
                      <button
                        onClick={() => handleEditCompanion(companion)}
                        className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          const companionId = companion.companion_id;
                          if (companionId) {
                            handleDeleteCompanion(companionId);
                          }
                        }}
                        className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAndSortedCompanions.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold text-white mb-2">No Companions Found</h3>
            <p className="text-dark-300">
              {searchTerm ? 'Try adjusting your search terms' : 'No companions available'}
            </p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedCompanion && (
        <EditCompanionModal
          companion={selectedCompanion}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedCompanion(null);
          }}
          onSave={handleSaveCompanion}
        />
      )}
    </div>
  );
};

export default AdminDashboard;